import {IntlShape, MessageDescriptor, defineMessages} from 'react-intl'
import {
  DOCUMENT_STATUS_OPTIONS,
  GENDER_OPTIONS,
  IMMIGRATION_STATUS_CHOICES,
  DEDUCTION_TYPE_OPTIONS,
  MARITAL_STATUS,
  METAL_LEVELS,
  PAYMENT_STATUS_OPTIONS,
  RELATIONSHIP_OPTIONS,
  TABS_ONE_USER,
} from './catalogs'

/**
 * Etiquetas traducidas de los enums del frontend que se pintaban en crudo.
 *
 * §10 del docs/i18n-project-plan.md: un enum de negocio que se pinta como
 * etiqueta lleva etiqueta traducida, mientras el valor en ingles sigue viajando
 * a la API. Viven aqui —al lado de `catalogs.ts`, que es quien tiene las listas
 * de valores— para que las varias pantallas que los pintan compartan UNA
 * traduccion en vez de inventarse cada una la suya.
 *
 * Antes de la consolidacion del slice 1.11.c.2.b, el mismo enum estaba
 * declarado en cuatro a siete archivos: `policies/ModalFilters`,
 * `policies/PoliciesPage`, `contacts/ContactsPage`, `onePolicy/PolicyFamilyTree`,
 * `recipients/recipientMessages`… Ademas de duplicar trabajo, varios de esos
 * mapas estaban **fuera de `defineMessages`** y solo funcionaban porque otro
 * archivo declaraba bien la misma llave (guia §17.bis).
 *
 * **Los ids son los que ya existian**, no se han renombrado: el valor de
 * consolidar esta en tener una sola declaracion, no en que el id sea bonito.
 * Por eso conviven prefijos `common.*` y `policies.*` aqui dentro. Lo unico que
 * se colapso fueron los duplicados de verdad — dos juegos de llaves distintos
 * con el mismo texto (ver `IMMIGRATION` y `PAYMENT_STATUS`).
 */

/** Opcion `{value, label}` de los catalogos de `catalogs.ts`. */
export type CatalogOption = {value: string; label: string}

type Messages = Record<string, MessageDescriptor>

/**
 * Etiqueta traducida de un valor. Si el valor no esta en el mapa se devuelve el
 * fallback (o el propio valor), para que un valor que anada el backend se siga
 * pintando en vez de desaparecer.
 */
const labelOf = (
  intl: IntlShape,
  messages: Messages,
  value?: string | null,
  fallback?: string
): string => {
  if (value == null || value === '') return fallback ?? ''
  return messages[value] ? intl.formatMessage(messages[value]) : fallback ?? value
}

/**
 * La lista de opciones con las etiquetas traducidas. Se calcula en render y
 * **no** muta el const de `catalogs.ts` (guia §17.10/§17.17): los valores en
 * ingles siguen viajando a la API.
 */
const translateOptions = (
  intl: IntlShape,
  options: readonly CatalogOption[],
  messages: Messages
): CatalogOption[] =>
  options.map((o) => ({
    value: o.value,
    label: messages[o.value] ? intl.formatMessage(messages[o.value]) : o.label,
  }))

/* -------------------------------------------------------------------------- */
/* Genero                                                                     */
/* -------------------------------------------------------------------------- */

/** Refleja `GENDER_OPTIONS`. Los tres ids ya eran `common.*` en todas partes. */
const GENDER: Messages = defineMessages({
  Male: {id: 'common.gender.male', defaultMessage: 'Male'},
  Female: {id: 'common.gender.female', defaultMessage: 'Female'},
  Other: {id: 'common.option.other', defaultMessage: 'Other'},
}) as Messages

export const genderLabel = (intl: IntlShape, value?: string | null, fallback?: string): string =>
  labelOf(intl, GENDER, value, fallback)

export const translatedGenders = (intl: IntlShape): CatalogOption[] =>
  translateOptions(intl, GENDER_OPTIONS, GENDER)

/* -------------------------------------------------------------------------- */
/* Nivel de metal                                                             */
/* -------------------------------------------------------------------------- */

/** Refleja `METAL_LEVELS`. El value es el numero, no el nombre del metal. */
const METAL_LEVEL: Messages = defineMessages({
  '5': {id: 'policies.list.filters.metal.platinum', defaultMessage: 'Platinum'},
  '4': {id: 'policies.list.filters.metal.gold', defaultMessage: 'Gold'},
  '3': {id: 'policies.list.filters.metal.silver', defaultMessage: 'Silver'},
  '2': {id: 'policies.list.filters.metal.bronze', defaultMessage: 'Bronze'},
  '1': {id: 'policies.list.filters.metal.catastrophic', defaultMessage: 'Catastrophic'},
}) as Messages

export const metalLevelLabel = (
  intl: IntlShape,
  value?: string | number | null,
  fallback?: string
): string => labelOf(intl, METAL_LEVEL, value == null ? null : String(value), fallback)

export const translatedMetalLevels = (intl: IntlShape): CatalogOption[] =>
  translateOptions(intl, METAL_LEVELS, METAL_LEVEL)

/* -------------------------------------------------------------------------- */
/* Estatus migratorio                                                         */
/* -------------------------------------------------------------------------- */

/**
 * Refleja `IMMIGRATION_STATUS_CHOICES`. Habia **dos** juegos de llaves con el
 * mismo texto y la misma traduccion: `policies.list.filters.im.*` (lista de
 * polizas y destinatarios) y `policies.list.wizard.holder.immigration.*` (ficha
 * de poliza). Gana el primero, que tenia mas consumidores; el segundo se retiro.
 */
const IMMIGRATION: Messages = defineMessages({
  CITIZEN: {id: 'policies.list.filters.im.citizen', defaultMessage: 'Citizen'},
  RESIDENT: {id: 'policies.list.filters.im.resident', defaultMessage: 'Resident'},
  ASYLUM: {id: 'policies.list.filters.im.asylum', defaultMessage: 'Asylum Seeker'},
  EAD: {
    id: 'policies.list.filters.im.ead',
    defaultMessage: 'Employment Authorization Document (EAD)',
  },
  TPS: {
    id: 'policies.list.filters.im.tps',
    defaultMessage: 'Temporary Protected Status (TPS)',
  },
  HUMANITARIAN_PAROLE: {
    id: 'policies.list.filters.im.humanitarianParole',
    defaultMessage: 'Humanitarian Parole',
  },
  OTHER: {id: 'common.option.other', defaultMessage: 'Other'},
  UNDOCUMENTED: {id: 'policies.list.filters.im.undocumented', defaultMessage: 'Undocumented'},
}) as Messages

export const immigrationStatusLabel = (
  intl: IntlShape,
  value?: string | null,
  fallback?: string
): string => labelOf(intl, IMMIGRATION, value, fallback)

export const translatedImmigrationStatuses = (intl: IntlShape): CatalogOption[] =>
  translateOptions(intl, IMMIGRATION_STATUS_CHOICES, IMMIGRATION)

/**
 * El mismo catalogo indexado por la etiqueta inglesa en vez de por el codigo.
 * `onePolicy/MemberAdd` guarda la frase (`'Asylum Seeker'`) donde el resto de la
 * app guarda el codigo (`'ASYLUM'`). Es una inconsistencia de datos anterior a
 * la migracion; se respeta el valor que graba y solo se comparte la traduccion.
 */
const IMMIGRATION_BY_LABEL: Messages = {
  Citizen: IMMIGRATION.CITIZEN,
  Resident: IMMIGRATION.RESIDENT,
  'Asylum Seeker': IMMIGRATION.ASYLUM,
  'Employment Authorization Document (EAD)': IMMIGRATION.EAD,
  'Temporary Protected Status (TPS)': IMMIGRATION.TPS,
  'Humanitarian Parole': IMMIGRATION.HUMANITARIAN_PAROLE,
  Undocumented: IMMIGRATION.UNDOCUMENTED,
  Other: IMMIGRATION.OTHER,
}

export const immigrationStatusLabelByName = (
  intl: IntlShape,
  value?: string | null,
  fallback?: string
): string => labelOf(intl, IMMIGRATION_BY_LABEL, value, fallback)

/* -------------------------------------------------------------------------- */
/* Estado del documento                                                       */
/* -------------------------------------------------------------------------- */

/** Refleja `DOCUMENT_STATUS_OPTIONS`. */
const DOCUMENT_STATUS: Messages = defineMessages({
  ACTIVE: {id: 'policies.list.filters.ds.active', defaultMessage: 'Active'},
  PENDING: {id: 'policies.list.filters.ds.pending', defaultMessage: 'Pending'},
  PROCESSING: {id: 'policies.list.filters.ds.processing', defaultMessage: 'Processing'},
  DECLINED: {id: 'policies.list.filters.ds.declined', defaultMessage: 'Declined'},
  COMPLETED: {id: 'policies.list.filters.ds.completed', defaultMessage: 'Completed'},
}) as Messages

export const documentStatusLabel = (
  intl: IntlShape,
  value?: string | null,
  fallback?: string
): string => labelOf(intl, DOCUMENT_STATUS, value, fallback)

export const translatedDocumentStatuses = (intl: IntlShape): CatalogOption[] =>
  translateOptions(intl, DOCUMENT_STATUS_OPTIONS, DOCUMENT_STATUS)

/* -------------------------------------------------------------------------- */
/* Estado de pago                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Refleja `PAYMENT_STATUS_OPTIONS`. Tambien tenia dos juegos:
 * `policies.list.filters.ps.*` y estos `common.paymentStatus.*`. Gana `common.*`
 * por ser el transversal, y de paso arregla el ingles de `PAGA_POR_SU_CUENTA`,
 * que en el juego retirado estaba en espanol tanto en EN como en ES.
 */
const PAYMENT_STATUS: Messages = defineMessages({
  PAID: {id: 'common.paymentStatus.paid', defaultMessage: 'Paid'},
  FAILED: {id: 'common.paymentStatus.failed', defaultMessage: 'Failed'},
  PENDING: {id: 'common.paymentStatus.pending', defaultMessage: 'Pending'},
  AUTO_PAY: {id: 'common.paymentStatus.autoPay', defaultMessage: 'Auto Pay'},
  NOT_APPLICABLE: {
    id: 'common.paymentStatus.notApplicable',
    defaultMessage: 'Not Applicable ($0)',
  },
  PAGA_POR_SU_CUENTA: {
    id: 'common.paymentStatus.selfPay',
    defaultMessage: 'Pays on their own',
  },
}) as Messages

/**
 * `PENDING` → "Pendiente". Los valores desconocidos pasan sin tocar, para que un
 * estado que anada el backend se siga pintando en vez de desaparecer.
 */
export const paymentStatusLabel = (
  intl: IntlShape,
  status?: string | null,
  fallback?: string
): string => labelOf(intl, PAYMENT_STATUS, status, fallback)

export const translatedPaymentStatuses = (intl: IntlShape): CatalogOption[] =>
  translateOptions(intl, PAYMENT_STATUS_OPTIONS, PAYMENT_STATUS)

/* -------------------------------------------------------------------------- */
/* Parentesco                                                                 */
/* -------------------------------------------------------------------------- */

/** Refleja `RELATIONSHIP_OPTIONS`. El value es la propia frase en ingles. */
const RELATIONSHIP: Messages = defineMessages({
  Self: {id: 'policies.detail.form.member.relationship.self', defaultMessage: 'Self'},
  Spouse: {id: 'policies.detail.form.member.relationship.spouse', defaultMessage: 'Spouse'},
  Child: {id: 'policies.detail.form.member.relationship.child', defaultMessage: 'Child'},
  'Mother or Father': {
    id: 'policies.detail.form.member.relationship.parent',
    defaultMessage: 'Mother or Father',
  },
  'Life Partner': {
    id: 'policies.detail.form.member.relationship.lifePartner',
    defaultMessage: 'Life Partner',
  },
  'Brother or Sister': {
    id: 'policies.detail.form.member.relationship.sibling',
    defaultMessage: 'Brother or Sister',
  },
  'Nephew or Niece': {
    id: 'policies.detail.form.member.relationship.niblings',
    defaultMessage: 'Nephew or Niece',
  },
  'Stepson or Stepdaughter': {
    id: 'policies.detail.form.member.relationship.stepchild',
    defaultMessage: 'Stepson or Stepdaughter',
  },
  Ward: {id: 'policies.detail.form.member.relationship.ward', defaultMessage: 'Ward'},
  'Grandson or Granddaughter': {
    id: 'policies.detail.form.member.relationship.grandchild',
    defaultMessage: 'Grandson or Granddaughter',
  },
  'Sponsored Dependent': {
    id: 'policies.detail.form.member.relationship.sponsoredDependent',
    defaultMessage: 'Sponsored Dependent',
  },
  'Collateral Dependent': {
    id: 'policies.detail.form.member.relationship.collateralDependent',
    defaultMessage: 'Collateral Dependent',
  },
  'Foster Child': {
    id: 'policies.detail.form.member.relationship.fosterChild',
    defaultMessage: 'Foster Child',
  },
  'Ex-Spouse': {
    id: 'policies.detail.form.member.relationship.exSpouse',
    defaultMessage: 'Ex-Spouse',
  },
  'Adopted Child': {
    id: 'policies.detail.form.member.relationship.adoptedChild',
    defaultMessage: 'Adopted Child',
  },
  'Court Appointed Guardian': {
    id: 'policies.detail.form.member.relationship.guardian',
    defaultMessage: 'Court Appointed Guardian',
  },
  'Dependent of a Minor Dependent': {
    id: 'policies.detail.form.member.relationship.dependentOfMinor',
    defaultMessage: 'Dependent of a Minor Dependent',
  },
  'Other Relative': {
    id: 'policies.detail.form.member.relationship.otherRelative',
    defaultMessage: 'Other Relative',
  },
  'Other Relationship': {
    id: 'policies.detail.form.member.relationship.otherRelationship',
    defaultMessage: 'Other Relationship',
  },
}) as Messages

export const relationshipLabel = (intl: IntlShape, value?: string | null, fallback?: string): string =>
  labelOf(intl, RELATIONSHIP, value, fallback)

export const translatedRelationships = (intl: IntlShape): CatalogOption[] =>
  translateOptions(intl, RELATIONSHIP_OPTIONS, RELATIONSHIP)

/* -------------------------------------------------------------------------- */
/* Estado civil                                                               */
/* -------------------------------------------------------------------------- */

/** Refleja `MARITAL_STATUS`, que es una lista plana de strings, no de opciones. */
const MARITAL: Messages = defineMessages({
  Married: {id: 'policies.detail.form.member.marital.married', defaultMessage: 'Married'},
  'Head of Household': {
    id: 'policies.detail.form.member.marital.headOfHousehold',
    defaultMessage: 'Head of Household',
  },
  Single: {id: 'policies.detail.form.member.marital.single', defaultMessage: 'Single'},
  Other: {id: 'common.option.other', defaultMessage: 'Other'},
}) as Messages

export const maritalStatusLabel = (intl: IntlShape, value?: string | null, fallback?: string): string =>
  labelOf(intl, MARITAL, value, fallback)

export const translatedMaritalStatuses = (intl: IntlShape): CatalogOption[] =>
  MARITAL_STATUS.map((value) => ({value, label: labelOf(intl, MARITAL, value)}))

/* -------------------------------------------------------------------------- */
/* Tipo de deduccion                                                          */
/* -------------------------------------------------------------------------- */

/** Refleja `DEDUCTION_TYPE_OPTIONS`. La opcion vacia es el placeholder. */
const DEDUCTION_TYPE: Messages = defineMessages({
  '': {id: 'admin.deductionTypes.selectType', defaultMessage: 'Select type'},
  FIXED_AMOUNT: {id: 'admin.deductionTypes.fixedAmount', defaultMessage: 'Fixed Amount'},
  PERCENTAGE: {id: 'admin.deductionTypes.percentage', defaultMessage: 'Percentage'},
}) as Messages

export const deductionTypeLabel = (intl: IntlShape, value?: string | null): string =>
  labelOf(intl, DEDUCTION_TYPE, value)

export const translatedDeductionTypes = (intl: IntlShape): CatalogOption[] =>
  translateOptions(intl, DEDUCTION_TYPE_OPTIONS, DEDUCTION_TYPE)

/* -------------------------------------------------------------------------- */
/* Pestanas de la ficha de usuario                                            */
/* -------------------------------------------------------------------------- */

/**
 * Refleja `TABS_ONE_USER`. El valor en ingles sigue siendo la clave de estado
 * (`setTabs('Edit user')`), solo cambia la etiqueta. El rule no lo veia porque
 * se pinta como `{tb}`, una variable.
 *
 * `Dify` no esta: es una marca, se escribe igual en los dos idiomas y
 * `labelOf` la devuelve tal cual (guia §17.14).
 */
const ONE_USER_TAB: Messages = defineMessages({
  Overview: {id: 'admin.oneUser.tab.overview', defaultMessage: 'Overview'},
  'Edit user': {id: 'admin.oneUser.tab.editUser', defaultMessage: 'Edit user'},
  Channels: {id: 'comms.field.channels', defaultMessage: 'Channels'},
  Deductions: {id: 'admin.oneUser.deductions.title', defaultMessage: 'Deductions'},
  Permissions: {id: 'admin.oneUser.permissions.title', defaultMessage: 'Permissions'},
  'Email Settings': {id: 'admin.oneUser.tab.emailSettings', defaultMessage: 'Email Settings'},
}) as Messages

export const oneUserTabLabel = (intl: IntlShape, tab: (typeof TABS_ONE_USER)[number]): string =>
  labelOf(intl, ONE_USER_TAB, tab)
